import { useEffect, useMemo, useState } from "react";
import PageBreadcrumbs from "../layouts/PageBreadcrumbs.js";
import { SkeletonTable } from "../components/Skeleton.js";
import { api, ApiError, DatasetDefinition } from "../lib/api.js";
import { useTableState } from "../lib/useTableState.js";
import TableToolbar from "../components/TableToolbar.js";

type WarehouseRow = Record<string, unknown>;

export default function DataPreview() {
  const [datasets, setDatasets] = useState<DatasetDefinition[]>([]);
  const [selected, setSelected] = useState<string>("");
  const [dimension, setDimension] = useState<string>("");
  const [metric, setMetric] = useState<string>("");
  const [period, setPeriod] = useState<string>("");
  const [limit, setLimit] = useState<number>(50);
  const [rows, setRows] = useState<WarehouseRow[]>([]);
  const [summary, setSummary] = useState<Record<string, unknown> | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api.datasets()
      .then((d) => {
        const list = d.datasets ?? [];
        setDatasets(list);
        if (list.length > 0) {
          setSelected(list[0].id);
        }
      })
      .catch((e) => setError(e instanceof ApiError ? e.message : String(e)));
  }, []);

  const activeDataset = useMemo(
    () => datasets.find((d) => d.id === selected),
    [datasets, selected]
  );

  useEffect(() => {
    if (!activeDataset) return;
    setDimension(activeDataset.dimensions[0] ?? "");
    setMetric(activeDataset.measures[0] ?? "");
    setPeriod("");
  }, [activeDataset]);

  const runPreview = async () => {
    if (!activeDataset) return;
    setLoading(true);
    setError(null);
    try {
      // dimension/period free-form params from the legacy preview shape
      // map onto groupBy + period filter on the new helper. The `limit`
      // parameter is no longer threaded through — the helper caps at 50
      // by default which matches the old preview UX.
      void limit;
      const groupByCandidates: ReadonlyArray<"metric" | "program" | "period" | "orgUnit"> = ["metric", "program", "period", "orgUnit"];
      const groupBy = (groupByCandidates as readonly string[]).includes(dimension) ? dimension as "metric" | "program" | "period" | "orgUnit" : "metric";
      const [grouped, sum] = await Promise.all([
        api.warehouseGrouped({
          groupBy,
          metricIds: metric ? [metric] : undefined,
          periodIds: period ? [period] : undefined
        }),
        api.warehouseSummary().catch(() => null)
      ]);
      setRows(grouped.rows ?? []);
      setSummary(sum as Record<string, unknown> | null);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  };

  const columns = useMemo(() => {
    if (rows.length === 0) return [] as string[];
    const keys = new Set<string>();
    for (const row of rows) {
      for (const k of Object.keys(row)) keys.add(k);
    }
    return Array.from(keys);
  }, [rows]);

  const t = useTableState(rows as unknown as Array<Record<string, unknown>>, {
    initialPageSize: 25
  });

  return (
    <div className="page">
      <PageBreadcrumbs />
      <header className="page-head">
        <div>
          <h1>Data preview</h1>
          <p>Sample rows from a published dataset. Use this to verify warehouse output before publishing a portal or forwarding feed.</p>
        </div>
      </header>

      {error && <div className="alert alert-error">{error}</div>}

      <section className="section">
        <div className="card">
          <div className="card-body" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 12, alignItems: "end" }}>
            <label>
              <div className="soft" style={{ marginBottom: 4 }}>Dataset</div>
              <select value={selected} onChange={(e) => setSelected(e.target.value)} disabled={datasets.length === 0}>
                {datasets.length === 0 && <option value="">— no datasets —</option>}
                {datasets.map((d) => (
                  <option key={d.id} value={d.id}>{d.name} ({d.datasetType})</option>
                ))}
              </select>
            </label>
            <label>
              <div className="soft" style={{ marginBottom: 4 }}>Dimension</div>
              <select value={dimension} onChange={(e) => setDimension(e.target.value)} disabled={!activeDataset}>
                <option value="">— any —</option>
                {activeDataset?.dimensions.map((d) => <option key={d} value={d}>{d}</option>)}
              </select>
            </label>
            <label>
              <div className="soft" style={{ marginBottom: 4 }}>Metric</div>
              <select value={metric} onChange={(e) => setMetric(e.target.value)} disabled={!activeDataset}>
                <option value="">— any —</option>
                {activeDataset?.measures.map((m) => <option key={m} value={m}>{m}</option>)}
              </select>
            </label>
            <label>
              <div className="soft" style={{ marginBottom: 4 }}>Period (YYYY-MM)</div>
              <input type="text" value={period} onChange={(e) => setPeriod(e.target.value)} placeholder="2026-04" />
            </label>
            <label>
              <div className="soft" style={{ marginBottom: 4 }}>Row limit</div>
              <input
                type="number"
                value={limit}
                min={1}
                max={500}
                onChange={(e) => setLimit(Math.max(1, Math.min(500, Number(e.target.value) || 1)))}
              />
            </label>
            <button type="button" className="btn btn-primary" onClick={runPreview} disabled={loading || !activeDataset}>
              {loading ? "Running…" : "Preview"}
            </button>
          </div>
        </div>
      </section>

      {activeDataset && (
        <section className="section">
          <div className="section-head">
            <h2>{activeDataset.name}</h2>
          </div>
          <div className="stat-grid">
            <div className="stat">
              <div className="stat-label">Rows</div>
              <div className="stat-value">{activeDataset.rowCount.toLocaleString()}</div>
              <div className="stat-hint">last refresh {new Date(activeDataset.lastRefreshedAt).toLocaleString()}</div>
            </div>
            <div className="stat">
              <div className="stat-label">Sensitivity</div>
              <div className="stat-value">{activeDataset.sensitivity}</div>
            </div>
            <div className="stat">
              <div className="stat-label">Type</div>
              <div className="stat-value">{activeDataset.datasetType.replace(/-/g, " ")}</div>
            </div>
            {summary && (
              <div className="stat">
                <div className="stat-label">Warehouse total</div>
                <div className="stat-value">
                  {typeof summary.total === "number" ? summary.total.toLocaleString() : JSON.stringify(summary.total ?? summary)}
                </div>
              </div>
            )}
          </div>
        </section>
      )}

      <section className="section">
        <div className="section-head">
          <h2>Sample rows ({rows.length})</h2>
        </div>
        {loading ? (
          <SkeletonTable rows={5} columns={4} />
        ) : rows.length === 0 ? (
          <div className="empty">No rows yet — press Preview to run a warehouse query.</div>
        ) : (
          <div className="table-scroll">
            <TableToolbar
              search={t.search} setSearch={t.setSearch}
              page={t.page} setPage={t.setPage}
              pageSize={t.pageSize} setPageSize={t.setPageSize}
              totalPages={t.totalPages} totalRows={t.totalRows}
              hideExport
              searchPlaceholder="Search rows…"
            />
            <table className="data-table">
              <thead>
                <tr>
                  {columns.map((c) => <th key={c}>{c}</th>)}
                </tr>
              </thead>
              <tbody>
                {(t.paged as unknown as WarehouseRow[]).map((row, idx) => (
                  <tr key={idx}>
                    {columns.map((c) => {
                      const v = row[c];
                      const display = v === null || v === undefined
                        ? "—"
                        : typeof v === "object"
                          ? JSON.stringify(v)
                          : String(v);
                      return <td key={c}>{display}</td>;
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
